import React, {useRef} from 'react';
import styled from 'styled-components';
import {
	ColumnSizingState,
	SortingState,
	VisibilityState,
	flexRender,
	functionalUpdate,
	getCoreRowModel,
	getExpandedRowModel,
	getSortedRowModel,
	useReactTable,
} from '@tanstack/react-table';
import {useVirtualizer} from '@tanstack/react-virtual';
import {closestCenter, DndContext, DragEndEvent, KeyboardSensor, PointerSensor, useSensor, useSensors} from '@dnd-kit/core';
import {arrayMove, horizontalListSortingStrategy, SortableContext, sortableKeyboardCoordinates} from '@dnd-kit/sortable';
import {Button, Text, tokensForThemeProvider} from '@forecast-it/design-system';
import {ReportRow} from '../data/reportRowModel';
import {ReportDefinitionV1Tasks} from '../contracts/report-definition.types';
import {
	ACTIONS_COLUMN_ID,
	EXPANDER_COLUMN_ID,
	FAVORITE_COLUMN_ID,
	FIXED_COLUMN_IDS,
	useReportTableColumns,
} from './useReportTableColumns';
import {DraggableHeader} from './DraggableHeader';
import {GroupHeaderRow} from './useClientGrouping';
import {formatDuration} from './durationFormat';

const tokens = tokensForThemeProvider.sky.sys;

export interface ReportTableState {
	sorting: SortingState;
	columnOrder: string[];
	columnSizing: ColumnSizingState;
	columnVisibility: VisibilityState;
}

export interface ReportTableProps {
	rows: ReportRow[]; // already grouped/pre-transformed (Task 4 wraps)
	definition: ReportDefinitionV1Tasks;
	tableState: ReportTableState;
	onTableStateChange: (patch: Partial<ReportTableState>) => void;
	onToggleFavorite: (rowId: string) => void;
	fieldLabels?: Record<string, string>;
}

const TableScroll = styled.div`
	width: 100%;
	max-height: 70vh;
	overflow: auto;
`;

/** `table-layout: fixed` is required, not cosmetic: only the visible row slice is mounted, so the
 * default `auto` layout would recompute column widths from whichever rows happen to be mounted and
 * the columns would visibly resize as you scroll — drifting out of alignment with the sticky header,
 * whose `<th>` widths come from `header.getSize()`. Fixed layout takes the widths from the header
 * row alone, so they stay stable while scrolling and a column resize moves the body cells with it.
 * `width: max-content` (floored at the container via `min-width`) keeps those header widths exact
 * rather than letting the browser scale them to fit; wider-than-container tables scroll instead. */
const StyledTable = styled.table`
	width: max-content;
	min-width: 100%;
	table-layout: fixed;
	border-collapse: collapse;
	${tokens.font.body['2']}

	thead th {
		position: sticky;
		top: 0;
		z-index: 1;
		background: ${tokens.color.background.primary['1']};
	}
`;

const HeaderRow = styled.tr`
	border-bottom: 1px solid ${tokens.color.border.enabled};
`;

const BodyRow = styled.tr<{$contextOnly: boolean}>`
	border-bottom: 1px solid ${tokens.color.border.decorative};
	color: ${({$contextOnly}) => ($contextOnly ? tokens.color.text.disabled.onlight : tokens.color.text.content.main)};
	font-style: ${({$contextOnly}) => ($contextOnly ? 'italic' : 'normal')};
`;

const BodyCell = styled.td`
	padding: ${tokens.spacing['450']} ${tokens.spacing['400']};
	vertical-align: middle;
`;

const GroupLabel = styled.span`
	font-weight: 600;
`;

/** Narrows a row to a `GroupHeaderRow` (see `useClientGrouping`) so its label/count/totals can be
 * rendered instead of per-column data cells. */
function isGroupHeaderRow(row: ReportRow): row is GroupHeaderRow {
	return (row as GroupHeaderRow).isGroupHeader === true;
}

/** Pure helper for `onDragEnd`: reorders `order` by moving `activeId` to `overId`'s position via
 * `arrayMove`. Returns `order` unchanged (same reference) if either id is a fixed column
 * (`__`-prefixed, e.g. `__expander`/`__actions`) or either id is not present in `order` — fixed
 * columns are never draggable/droppable targets, and unknown ids indicate a stale drag event. */
export function computeReorderedColumns(order: string[], activeId: string, overId: string): string[] {
	if (activeId === overId) {
		return order;
	}
	if (FIXED_COLUMN_IDS.includes(activeId) || FIXED_COLUMN_IDS.includes(overId)) {
		return order;
	}

	const activeIndex = order.indexOf(activeId);
	const overIndex = order.indexOf(overId);
	if (activeIndex === -1 || overIndex === -1) {
		return order;
	}

	return arrayMove(order, activeIndex, overIndex);
}

export function ReportTable({
	rows,
	definition,
	tableState,
	onTableStateChange,
	onToggleFavorite,
	fieldLabels,
}: ReportTableProps): JSX.Element {
	const columns = useReportTableColumns(definition, {fieldLabels, onToggleFavorite});

	const table = useReactTable({
		data: rows,
		columns,
		state: {
			sorting: tableState.sorting,
			columnOrder: tableState.columnOrder,
			columnSizing: tableState.columnSizing,
			columnVisibility: tableState.columnVisibility,
		},
		initialState: {
			expanded: true,
		},
		columnResizeMode: 'onChange',
		getCoreRowModel: getCoreRowModel(),
		getExpandedRowModel: getExpandedRowModel(),
		getSortedRowModel: getSortedRowModel(),
		getSubRows: row => row.children,
		onSortingChange: updater => {
			onTableStateChange({sorting: functionalUpdate(updater, tableState.sorting)});
		},
		onColumnOrderChange: updater => {
			onTableStateChange({columnOrder: functionalUpdate(updater, tableState.columnOrder)});
		},
		onColumnSizingChange: updater => {
			onTableStateChange({columnSizing: functionalUpdate(updater, tableState.columnSizing)});
		},
		onColumnVisibilityChange: updater => {
			onTableStateChange({columnVisibility: functionalUpdate(updater, tableState.columnVisibility)});
		},
	});

	// Current column order restricted to draggable data columns (fixed columns are excluded from
	// the SortableContext — they are never draggable or valid drop targets).
	const dataColumnOrder = table
		.getAllLeafColumns()
		.map(column => column.id)
		.filter(id => !FIXED_COLUMN_IDS.includes(id));

	// A distance activation constraint is required so a plain click on a header still fires its
	// sort `onClick`; without it the default PointerSensor starts a zero-distance drag on pointerdown
	// and swallows the click, so column sorting never triggers. A real drag (>8px) still reorders.
	const sensors = useSensors(
		useSensor(PointerSensor, {activationConstraint: {distance: 8}}),
		useSensor(KeyboardSensor, {coordinateGetter: sortableKeyboardCoordinates})
	);

	const handleDragEnd = (event: DragEndEvent): void => {
		const activeId = event.active.id;
		const overId = event.over?.id;
		if (overId == null) {
			return;
		}

		const currentOrder = tableState.columnOrder.length > 0 ? tableState.columnOrder : dataColumnOrder;
		const nextOrder = computeReorderedColumns(currentOrder, String(activeId), String(overId));
		if (nextOrder !== currentOrder) {
			onTableStateChange({columnOrder: nextOrder});
		}
	};

	const scrollRef = useRef<HTMLDivElement>(null);
	const rowModel = table.getRowModel().rows;
	const rowVirtualizer = useVirtualizer({
		count: rowModel.length,
		getScrollElement: () => scrollRef.current,
		estimateSize: () => 52,
		overscan: 12,
	});
	const virtualItems = rowVirtualizer.getVirtualItems();
	const totalSize = rowVirtualizer.getTotalSize();
	const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0;
	const paddingBottom = virtualItems.length > 0 ? totalSize - virtualItems[virtualItems.length - 1].end : 0;
	// The spacer rows that stand in for the unmounted slice above/below the window must span every
	// visible column — a single-cell spacer row is a one-column row the layout has to reconcile
	// against the real rows.
	const spacerColSpan = table.getVisibleLeafColumns().length;

	const renderRow = (row: (typeof rowModel)[number], flatIndex: number): JSX.Element => {
		if (isGroupHeaderRow(row.original)) {
			const groupRow = row.original;
			return (
				<BodyRow
					key={row.id}
					ref={rowVirtualizer.measureElement}
					data-index={flatIndex}
					$contextOnly={false}
					data-context-only={false}
				>
					{row.getVisibleCells().map((cell, cellIndex) => {
						const columnId = cell.column.id;

						// Expander/favourite and row-actions columns are not meaningful for a
						// synthetic group-header row (it isn't a real task) — render nothing,
						// but keep the expander's chevron so groups stay collapsible.
						if (columnId === EXPANDER_COLUMN_ID) {
							return (
								<BodyCell key={cell.id}>
									{row.getCanExpand() && (
										<Button
											iconOnly
											emphasis="low"
											icon={row.getIsExpanded() ? 'chevronUp' : 'chevronDown'}
											onPress={row.getToggleExpandedHandler()}
											aria-label="Toggle row expansion"
										/>
									)}
								</BodyCell>
							);
						}
						if (columnId === FAVORITE_COLUMN_ID || columnId === ACTIONS_COLUMN_ID) {
							return <BodyCell key={cell.id} />;
						}

						// Indent the first data cell (label + count) by depth, matching the
						// data-row indent so nested grouping levels visually nest.
						const isFirstDataCell = cellIndex === 1;
						if (isFirstDataCell) {
							return (
								<BodyCell
									key={cell.id}
									style={{
										paddingLeft: `calc(${tokens.spacing['300']} + ${row.depth * 20}px)`,
									}}
								>
									<Text>
										<GroupLabel>{groupRow.groupLabel}</GroupLabel> ({groupRow.groupCount})
									</Text>
								</BodyCell>
							);
						}

						// Per-column totals: blank (not "N/A") when the column has no duration
						// total for this group, or when units were mixed (suppressed).
						const total = groupRow.groupTotals[columnId];
						return (
							<BodyCell key={cell.id}>
								{total ? <Text>{formatDuration(total.minutes, total.unit)}</Text> : null}
							</BodyCell>
						);
					})}
				</BodyRow>
			);
		}

		return (
			<BodyRow
				key={row.id}
				ref={rowVirtualizer.measureElement}
				data-index={flatIndex}
				$contextOnly={!row.original.matchedFilter}
				data-context-only={!row.original.matchedFilter}
			>
				{row.getVisibleCells().map((cell, cellIndex) => {
					// Indent the first cell after the expander column by depth, so nested rows
					// visually nest under their parent regardless of column order/visibility.
					const isFirstDataCell = cellIndex === 1 && cell.column.id !== EXPANDER_COLUMN_ID;
					return (
						<BodyCell
							key={cell.id}
							style={
								isFirstDataCell
									? {paddingLeft: `calc(${tokens.spacing['300']} + ${row.depth * 20}px)`}
									: undefined
							}
						>
							{flexRender(cell.column.columnDef.cell, cell.getContext())}
						</BodyCell>
					);
				})}
			</BodyRow>
		);
	};

	return (
		<TableScroll ref={scrollRef}>
			<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
				<StyledTable>
					<thead>
						{table.getHeaderGroups().map(headerGroup => (
							<HeaderRow key={headerGroup.id}>
								<SortableContext items={dataColumnOrder} strategy={horizontalListSortingStrategy}>
									{headerGroup.headers.map(header => (
										<DraggableHeader key={header.id} header={header} />
									))}
								</SortableContext>
							</HeaderRow>
						))}
					</thead>
					<tbody>
						{paddingTop > 0 && (
							<tr aria-hidden={true}>
								<td colSpan={spacerColSpan} style={{height: paddingTop, padding: 0, border: 0}} />
							</tr>
						)}
						{virtualItems.map(virtualRow => renderRow(rowModel[virtualRow.index], virtualRow.index))}
						{paddingBottom > 0 && (
							<tr aria-hidden={true}>
								<td colSpan={spacerColSpan} style={{height: paddingBottom, padding: 0, border: 0}} />
							</tr>
						)}
					</tbody>
				</StyledTable>
			</DndContext>
		</TableScroll>
	);
}
